import React, { useState, useEffect, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { Search, X, CheckCircleFill, PersonFill } from 'react-bootstrap-icons';
import { ClientLocal } from 'views/Restaurant/ClientManagement/Clients/types/client.types';
import { getClientsByLocal } from 'crud/clients_api/local_clients';
import { toast } from 'react-toastify';
import './ClientSearch.scss';

interface ClientSearchProps {
  onClientSelect: (client: ClientLocal) => void;
  selectedClient: ClientLocal | null;
}

const ClientSearch: React.FC<ClientSearchProps> = ({ onClientSelect, selectedClient }) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [clients, setClients] = useState<ClientLocal[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      // Get local_id from localStorage
      const user = localStorage.getItem('user');
      const localId = user ? JSON.parse(user).local_id : null;
      if (!localId) {
        toast.error('No se pudo obtener el ID del local');
        return;
      }

      setInitialLoading(true);
      const response = await getClientsByLocal(localId);
      if (response && response.data) {
        setClients(response.data);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Error cargando clientes');
    } finally {
      setInitialLoading(false);
    }
  };

  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) {
      return clients;
    }

    const term = searchTerm.toLowerCase().trim();
    return clients.filter(client => {
      const fullName = `${client.username} ${client.middle_name || ''} ${client.last_name || ''}`.toLowerCase();
      const email = client.email?.toLowerCase() || '';
      const phone = client.phone_number?.toLowerCase() || '';

      return fullName.includes(term) || email.includes(term) || phone.includes(term);
    });
  }, [clients, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleClientClick = (client: ClientLocal) => {
    onClientSelect(client);
  };

  const formatPhoneNumber = (phone?: string) => {
    if (!phone) return 'Sin teléfono';
    return phone;
  };

  if (initialLoading) {
    return (
      <div className="client-search-loading">
        <Spinner animation="border" variant="primary" />
        <p>Cargando clientes...</p>
      </div>
    );
  }

  return (
    <div className="client-search-section">
      <h5 className="section-title">Buscar cliente registrado</h5>
      
      <div className="search-container">
        <div className="search-input-group">
          <span className="search-icon">
            <Search />
          </span>
          <input
            type="text"
            placeholder="Buscar por nombre, email o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-button" onClick={handleClearSearch}>
              <X size={18} />
            </button>
          )}
        </div>

        <div className="search-results-info">
          <p>
            {filteredClients.length} {filteredClients.length === 1 ? 'cliente encontrado' : 'clientes encontrados'}
          </p>
        </div>
      </div>

      <div className="clients-list">
        {filteredClients.length === 0 ? (
          <div className="no-results">
            <p>No se encontraron clientes</p>
            {searchTerm && <p className="suggestion">Intenta con otro término de búsqueda</p>}
          </div>
        ) : (
          filteredClients.map((client) => (
            <div
              key={client.client_id}
              className={`client-card ${selectedClient?.client_id === client.client_id ? 'selected' : ''}`}
              onClick={() => handleClientClick(client)}
            >
              {client.membership && (
                <div className="client-membership">
                  <span className="membership-badge">{client.membership.membership_name}</span>
                </div>
              )}
              <div className="client-avatar">
                <PersonFill size={20} />
              </div>
              <div className="client-info">
                <div className="client-name">
                  {client.username} {client.middle_name || ''} {client.last_name || ''}
                </div>
                <div className="client-contact">
                  <span className="client-email">{client.email}</span>
                  {client.phone_number && (
                    <>
                      <span className="separator">•</span>
                      <span className="client-phone">{formatPhoneNumber(client.phone_number)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedClient && (
        <div className="selected-client-summary">
          <CheckCircleFill className="check-icon" />
          <span>
            Cliente seleccionado: <strong>{selectedClient.username} {selectedClient.last_name}</strong>
          </span>
        </div>
      )}
    </div>
  );
};

export default ClientSearch;
